import { $$ } from '@utils';
import removeAccentMarkAndUppercase from '@utils/RemoveAccentMarkAndUppercase';

function init() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          const wrappers = node.matches('[data-au-dynamic-input-set]')
            ? [node]
            : node.querySelectorAll('[data-au-dynamic-input-set]');
          wrappers.forEach((wrapper) => initInstance(wrapper));
        });
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  $$('[data-au-dynamic-input-set]').forEach((wrapper) => {
    initInstance(wrapper);
  });
}

function initInstance(wrapper) {
  if (wrapper.dataset.dynamicInputSetInitialized === 'true') return;
  wrapper.dataset.dynamicInputSetInitialized = 'true';

  wrapper.querySelectorAll('[data-au-dynamic-input-set-field]').forEach((slot) => {
    const input = slot.querySelector('input, select, textarea');
    if (!input) return;

    if (slot.hasAttribute('data-au-dynamic-input-set-field-required')) {
      input.dataset.dynamicInputSetRequired = 'true';
      input.removeAttribute('required');
    }

    const pattern = slot.getAttribute('data-au-dynamic-input-set-field-pattern');
    if (pattern) {
      input.dataset.dynamicInputSetPattern = pattern;
      const msg = slot.getAttribute('data-au-dynamic-input-set-field-pattern-message');
      if (msg) input.dataset.dynamicInputSetPatternMessage = msg;
      input.removeAttribute('pattern');
    }
  });

  refreshAddState(wrapper);

  wrapper.addEventListener('input', (e) => {
    validatePattern(e.target);
    refreshAddState(wrapper);
  });
  wrapper.addEventListener('change', (e) => {
    validatePattern(e.target);
    refreshAddState(wrapper);
  });

  const addBtn = wrapper.querySelector('[data-au-dynamic-input-set-add]');
  if (addBtn) {
    addBtn.addEventListener('click', (e) => {
      if (addBtn.disabled || addBtn.hasAttribute('aria-disabled')) return;
      e.preventDefault();
      addItem(wrapper);
    });
  }

  wrapper.querySelectorAll('[data-au-dynamic-input-set-item-remove]').forEach((btn) => {
    bindRemove(wrapper, btn);
  });

  bindFormSubmit(wrapper);
}

function getFieldEls(wrapper) {
  return Array.from(wrapper.querySelectorAll('[data-au-dynamic-input-set-field]'))
    .map((slot) => {
      return slot.querySelector('input, select, textarea');
    })
    .filter(Boolean);
}

function getCurrentItemsCount(wrapper) {
  const list = wrapper.querySelector('[data-au-dynamic-input-set-items]');
  if (!list) return 0;
  return list.querySelectorAll('[data-au-dynamic-input-set-item]').length;
}

function getMaxItems(wrapper) {
  const max = parseInt(wrapper.dataset.auDynamicInputSetMax, 10);
  return Number.isFinite(max) && max > 0 ? max : Infinity;
}

function isAtMax(wrapper) {
  return getCurrentItemsCount(wrapper) >= getMaxItems(wrapper);
}

function getFieldValue(el) {
  if (el.tagName === 'SELECT') {
    const selectContainer = el.closest('[data-au-select]');
    if (selectContainer) {
      const textEl = selectContainer.querySelector('[data-au-select-text]');
      return textEl ? textEl.textContent.trim() : el.value;
    }
  }
  return el.value;
}

function allFieldsValid(wrapper) {
  return getFieldEls(wrapper).every((el) => {
    const isRequired = el.dataset.dynamicInputSetRequired === 'true';
    const value = getFieldValue(el);
    if (isRequired && !value) return false;
    if (!patternMatches(el, value)) return false;
    return true;
  });
}

function patternMatches(el, value) {
  if (!value) return true;
  const raw = el.dataset.dynamicInputSetPattern;
  if (!raw) return true;
  try {
    return new RegExp(raw).test(value);
  } catch {
    return true;
  }
}

function validatePattern(el) {
  if (!el || !el.dataset || !el.dataset.dynamicInputSetPattern) return;
  const formEl = el.closest('[data-au-form-element]');
  if (!formEl) return;
  const value = getFieldValue(el);
  if (!value || patternMatches(el, value)) {
    clearFieldPatternError(formEl, el);
  } else {
    showFieldPatternError(formEl, el, el.dataset.dynamicInputSetPatternMessage || '');
  }
}

function showFieldPatternError(formEl, input, message) {
  formEl.classList.add('au-form-element-error');
  if (input.tagName !== 'SELECT') input.classList.add('au-form-field-error');
  input.setAttribute('aria-invalid', 'true');

  let errEl = formEl.querySelector('[data-au-dynamic-input-set-field-pattern-error]');
  if (!errEl) {
    errEl = document.createElement('p');
    errEl.className = 'au-form-msg-error t-detail';
    errEl.setAttribute('data-au-dynamic-input-set-field-pattern-error', '');
    errEl.setAttribute('role', 'alert');
    errEl.setAttribute('aria-live', 'assertive');
    const sr = document.createElement('span');
    sr.className = 'au-visually-hidden';
    sr.textContent = 'Error: ';
    errEl.appendChild(sr);
    errEl.appendChild(document.createTextNode(message));
    formEl.appendChild(errEl);
  } else if (errEl.lastChild) {
    errEl.lastChild.nodeValue = message;
  }
}

function clearFieldPatternError(formEl, input) {
  formEl.classList.remove('au-form-element-error');
  if (input) {
    input.classList.remove('au-form-field-error');
    input.removeAttribute('aria-invalid');
  }
  const errEl = formEl.querySelector('[data-au-dynamic-input-set-field-pattern-error]');
  if (errEl) errEl.remove();
}

function refreshAddState(wrapper) {
  const addBtn = wrapper.querySelector('[data-au-dynamic-input-set-add]');
  if (!addBtn) return;

  const enable = !wrapper.matches('.au-dynamic-input-set--disabled') && !isAtMax(wrapper) && allFieldsValid(wrapper);

  setLinkDisabled(addBtn, !enable);
}

function setLinkDisabled(linkBtn, disabled) {
  linkBtn.classList.toggle('au-link--disabled', disabled);
  if (disabled) {
    linkBtn.setAttribute('aria-disabled', 'true');
    linkBtn.setAttribute('tabindex', '-1');
    if (linkBtn.tagName === 'BUTTON') linkBtn.disabled = true;
  } else {
    linkBtn.removeAttribute('aria-disabled');
    linkBtn.removeAttribute('tabindex');
    if (linkBtn.tagName === 'BUTTON') linkBtn.disabled = false;
  }
}

function addItem(wrapper) {
  const fieldEls = getFieldEls(wrapper);
  const values = fieldEls.map((el) => (getFieldValue(el) || '').trim()).filter(Boolean);
  if (values.length === 0) return;

  const list = wrapper.querySelector('[data-au-dynamic-input-set-items]');
  if (!list) return;

  const ariaLabelBase = wrapper.getAttribute('data-au-dynamic-input-set-item-aria-label') || 'Eliminar';

  const firstRequiredSlot = wrapper.querySelector(
    '[data-au-dynamic-input-set-field][data-au-dynamic-input-set-field-required]',
  );
  const firstRequiredEl = firstRequiredSlot?.querySelector('input, select, textarea');
  const firstRequiredLabel = firstRequiredSlot
    ? (firstRequiredSlot.getAttribute('data-au-dynamic-input-set-field-label') || '').trim()
    : '';
  const firstRequiredValue = firstRequiredEl ? (getFieldValue(firstRequiredEl) || '').trim() : '';

  const actionLabel = [ariaLabelBase, firstRequiredLabel, firstRequiredValue].filter(Boolean).join(' ');

  const item = buildItemNode(values, actionLabel, wrapper);
  list.appendChild(item);
  const itemsWrapper = wrapper.querySelector('[data-au-dynamic-input-set-items-wrapper]');
  if (itemsWrapper) itemsWrapper.classList.remove('au-hide');

  bindRemove(wrapper, item.querySelector('[data-au-dynamic-input-set-item-remove]'));

  resetFields(wrapper);

  if (isAtMax(wrapper)) {
    setFieldsDisabled(wrapper, true);
    showMaxMessage(wrapper, true);
  }

  clearWrapperError(wrapper);
  refreshAddState(wrapper);

  const labelEl = wrapper.querySelector('.au-dynamic-input-set__label');
  const labelText = labelEl ? removeAccentMarkAndUppercase(labelEl.textContent) : 'dynamic_input_set';
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'navigation',
    content_type: 'clickModulo',
    item_id: `${labelText}_anadir`,
  });
}

function getNextItemIndex(wrapper) {
  wrapper.dataset.dynamicInputSetItemCount = String(
    (parseInt(wrapper.dataset.dynamicInputSetItemCount || '0', 10) || 0) + 1,
  );
  return wrapper.dataset.dynamicInputSetItemCount;
}

function buildItemNode(values, actionLabel, wrapper) {
  const componentId = wrapper.dataset.auDynamicInputSetId || 'dynamic-input-set';
  const idx = getNextItemIndex(wrapper);
  const textId = `${componentId}-item-${idx}-text`;
  const actionId = `${componentId}-item-${idx}-action`;

  const fieldLabels = Array.from(wrapper.querySelectorAll('[data-au-dynamic-input-set-field]')).map(
    (slot) => slot.getAttribute('data-au-dynamic-input-set-field-label') || '',
  );

  const item = document.createElement('li');
  item.className = 'au-dynamic-input-set__item';
  item.setAttribute('data-au-dynamic-input-set-item', '');

  const text = document.createElement('p');
  text.className = 'au-dynamic-input-set__item-text t-detail';
  text.id = textId;

  values.forEach((value, i) => {
    const valueSpan = document.createElement('span');
    const fieldLabel = fieldLabels[i] || '';
    if (fieldLabel) {
      const hiddenFieldLabel = document.createElement('span');
      hiddenFieldLabel.className = 'au-visually-hidden';
      hiddenFieldLabel.textContent = `${fieldLabel}: `;
      valueSpan.appendChild(hiddenFieldLabel);
    }
    valueSpan.appendChild(document.createTextNode(value));
    text.appendChild(valueSpan);

    if (i < values.length - 1) {
      text.appendChild(document.createTextNode(' '));
      const sep = document.createElement('span');
      sep.className = 'au-dynamic-input-set__item-sep';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = '-';
      text.appendChild(sep);
      text.appendChild(document.createTextNode(' '));
    }
  });

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className =
    'au-button au-button--small au-button--ghost au-button--icon-only au-dynamic-input-set__item-remove';
  removeBtn.setAttribute('data-au-dynamic-input-set-item-remove', '');
  removeBtn.setAttribute('aria-labelledby', actionId);

  const iconEl = document.createElement('span');
  iconEl.className = 'material-symbols-rounded';
  iconEl.setAttribute('aria-hidden', 'true');
  iconEl.setAttribute('data-au-icon', '');
  iconEl.textContent = 'delete';
  removeBtn.appendChild(iconEl);

  const hiddenLabel = document.createElement('span');
  hiddenLabel.id = actionId;
  hiddenLabel.className = 'au-visually-hidden';
  hiddenLabel.textContent = actionLabel;
  removeBtn.appendChild(hiddenLabel);

  item.appendChild(text);
  item.appendChild(removeBtn);
  return item;
}

function bindRemove(wrapper, btn) {
  if (!btn || btn.dataset.auBound === 'true') return;
  btn.dataset.auBound = 'true';

  btn.addEventListener('click', () => {
    const item = btn.closest('[data-au-dynamic-input-set-item]');
    if (!item) return;
    item.remove();

    const list = wrapper.querySelector('[data-au-dynamic-input-set-items]');
    if (list && list.querySelectorAll('[data-au-dynamic-input-set-item]').length === 0) {
      const itemsWrapper = wrapper.querySelector('[data-au-dynamic-input-set-items-wrapper]');
      if (itemsWrapper) itemsWrapper.classList.add('au-hide');
    }

    if (!isAtMax(wrapper)) {
      setFieldsDisabled(wrapper, false);
      showMaxMessage(wrapper, false);
    }

    refreshAddState(wrapper);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'navigation',
      content_type: 'clickModulo',
      item_id: 'dynamic_input_set_borrar',
    });
  });
}

function resetFields(wrapper) {
  getFieldEls(wrapper).forEach((el) => {
    if (el.tagName === 'SELECT') {
      const selectContainer = el.closest('[data-au-select]');
      if (selectContainer) {
        const textEl = selectContainer.querySelector('[data-au-select-text]');
        if (textEl) textEl.textContent = '';
        selectContainer.querySelectorAll('[data-au-option]').forEach((opt) => opt.removeAttribute('selected'));
        selectContainer.querySelectorAll('[data-au-listbox-option]').forEach((opt) => {
          opt.classList.remove('au-active');
          if (opt.hasAttribute('aria-selected')) opt.setAttribute('aria-selected', 'false');
        });
      }
    }
    el.value = '';
    el.dispatchEvent(new Event('change', { bubbles: true }));
    const formEl = el.closest('[data-au-form-element]');
    if (formEl) clearFieldPatternError(formEl, el);
  });
}

function showMaxMessage(wrapper, show) {
  const msg = wrapper.querySelector('[data-au-dynamic-input-set-max-msg]');
  if (!msg) return;
  msg.classList.toggle('au-hide', !show);
}

function getErrorMessage(wrapper) {
  return wrapper.getAttribute('data-au-dynamic-input-set-error-message') || 'Debes añadir un elemento';
}

function showWrapperError(wrapper) {
  const errorContainer = wrapper.querySelector('[data-au-dynamic-input-set-error]');
  if (!errorContainer) return;
  wrapper.classList.add('au-form-element-error');
  if (errorContainer.querySelector('[data-au-dynamic-input-set-required-error]')) return;

  const msg = document.createElement('p');
  msg.className = 'au-form-msg-error t-detail';
  msg.setAttribute('data-au-dynamic-input-set-required-error', '');

  const sr = document.createElement('span');
  sr.className = 'au-visually-hidden';
  sr.textContent = 'Error: ';
  msg.appendChild(sr);
  msg.appendChild(document.createTextNode(getErrorMessage(wrapper)));

  errorContainer.appendChild(msg);
}

function clearWrapperError(wrapper) {
  const errorContainer = wrapper.querySelector('[data-au-dynamic-input-set-error]');
  if (!errorContainer) return;
  wrapper.classList.remove('au-form-element-error');
  const existing = errorContainer.querySelector('[data-au-dynamic-input-set-required-error]');
  if (existing) existing.remove();
}

function bindFormSubmit(wrapper) {
  const form = wrapper.closest('form');
  if (!form) return;
  if (form.dataset.dynamicInputSetSubmitBound === 'true') return;
  form.dataset.dynamicInputSetSubmitBound = 'true';

  form.addEventListener(
    'submit',
    (e) => {
      let firstInvalid = null;
      form.querySelectorAll('[data-au-dynamic-input-set][data-au-dynamic-input-set-required]').forEach((w) => {
        if (getCurrentItemsCount(w) === 0) {
          showWrapperError(w);
          if (!firstInvalid) firstInvalid = w;
        } else {
          clearWrapperError(w);
        }
      });

      if (firstInvalid) {
        e.preventDefault();

        requestAnimationFrame(() => {
          const firstErrorWrapper = form.querySelector('[data-au-form-element].au-form-element-error');
          if (firstErrorWrapper !== firstInvalid) return;

          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const firstField = getFieldEls(firstInvalid)[0];
          if (firstField && typeof firstField.focus === 'function') {
            firstField.focus({ preventScroll: true });
          }
        });
      }
    },
    true,
  );
}

function setFieldsDisabled(wrapper, disabled) {
  wrapper.classList.toggle('au-dynamic-input-set--disabled', disabled);
  getFieldEls(wrapper).forEach((el) => {
    el.disabled = disabled;
    const container = el.closest('[data-au-form-element]');
    if (!container) return;
    container.classList.toggle('au-input-base--disabled', disabled && container.classList.contains('au-input-base'));
    container.classList.toggle('au-select--disabled', disabled && container.classList.contains('au-select'));
  });
}

export default { init };
